/** Soluppgång/solnedgång (NOAA-approximation). Standard: Stockholm. */
export const DEFAULT_LAT = 59.3293;
export const DEFAULT_LON = 18.0686;

const rad = Math.PI / 180;

/** Returnerar { sunrise, sunset } som Date, eller null vid midnattssol/polarnatt. */
export function getSunTimes(
  date: Date,
  lat = DEFAULT_LAT,
  lon = DEFAULT_LON,
): { sunrise: Date; sunset: Date } | null {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const dayOfYear = Math.floor(
    (Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - start) / 86400000,
  );

  const fracYear = ((2 * Math.PI) / 365) * (dayOfYear - 1);
  const eqTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(fracYear) -
      0.032077 * Math.sin(fracYear) -
      0.014615 * Math.cos(2 * fracYear) -
      0.040849 * Math.sin(2 * fracYear));
  const decl =
    0.006918 -
    0.399912 * Math.cos(fracYear) +
    0.070257 * Math.sin(fracYear) -
    0.006758 * Math.cos(2 * fracYear) +
    0.000907 * Math.sin(2 * fracYear) -
    0.002697 * Math.cos(3 * fracYear) +
    0.00148 * Math.sin(3 * fracYear);

  const cosHa =
    Math.cos(90.833 * rad) / (Math.cos(lat * rad) * Math.cos(decl)) -
    Math.tan(lat * rad) * Math.tan(decl);
  if (cosHa > 1 || cosHa < -1) return null;
  const ha = Math.acos(cosHa) / rad;

  const sunriseMin = 720 - 4 * (lon + ha) - eqTime;
  const sunsetMin = 720 - 4 * (lon - ha) - eqTime;

  const base = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return {
    sunrise: new Date(base + sunriseMin * 60000),
    sunset: new Date(base + sunsetMin * 60000),
  };
}

/** Är det dagsljus just nu på given plats? */
export function isDaylight(now = new Date(), lat = DEFAULT_LAT, lon = DEFAULT_LON): boolean {
  const t = getSunTimes(now, lat, lon);
  if (!t) {
    // Midnattssol eller polarnatt: falla tillbaka på solhöjd via månad/latitud
    const month = now.getUTCMonth();
    return lat > 0 ? month >= 3 && month <= 8 : !(month >= 3 && month <= 8);
  }
  return now >= t.sunrise && now < t.sunset;
}

/** Nästa tidpunkt då läget byts (soluppgång eller solnedgång). */
export function nextSwitch(now = new Date(), lat = DEFAULT_LAT, lon = DEFAULT_LON): Date {
  const today = getSunTimes(now, lat, lon);
  if (today) {
    if (now < today.sunrise) return today.sunrise;
    if (now < today.sunset) return today.sunset;
  }
  const tomorrow = getSunTimes(new Date(now.getTime() + 86400000), lat, lon);
  return tomorrow ? tomorrow.sunrise : new Date(now.getTime() + 3600000);
}
